// src/adminNav/index.js — 🔍 admin global search (v129.63).
//
// Matt: "I'm struggling to find things… a search bar would be useful."
// 22 top-level tabs with sub-tabs hiding inside five of them is a memory
// test. This is the answer-in-one-keystroke: Ctrl/Cmd+K (or the 🔍 button
// injected into the tab bar) opens a command palette that indexes every
// tab, sub-tab and the common words people actually say ("holiday",
// "rota", "pin", "bonus") and JUMPS there on Enter.
//
// Deliberately read-only navigation: it only ever calls the SAME
// switchTab/switchGroupSub globals the tab bar calls, so role gates and
// tab-visibility overrides keep working — a hidden tab stays hidden
// because the button it would click does not exist.

// ── The index. label = what the row shows; syn = what people type. ──────────
export const NAV_INDEX = [
  { label: '📊 Dashboard', tab: 'dashboard', syn: ['home', 'overview', 'today'] },
  { label: '☀️ Brief', tab: 'ambrief', syn: ['morning', 'am brief', 'daily'] },
  { label: '🏬 Store overview', tab: 'storeoverview', syn: ['store overview', 'store visit', 'store snapshot', 'visit prep', 'store stats'] },
  { label: '💯 Scorecard', tab: 'scorecard', syn: ['scorecard', 'score', 'kpi score', 'bonus', 'store score', 'staff score', 'payroll'] },   // v136.41
  { label: '👥 People → Staff', tab: 'people', sub: 'staff', syn: ['employees', 'team', 'roster', 'pin', 'photo', 'contract'] },
  { label: '👥 People → Managers', tab: 'people', sub: 'managers', syn: ['accounts', 'logins', 'admin users', 'password'] },
  { label: '👥 People → TOIL overview', tab: 'people', sub: 'toil_overview', syn: ['toil', 'time off in lieu', 'balances', 'holiday', 'sick'] },
  { label: '👥 People → Requests', tab: 'people', sub: 'requests', syn: ['leave request', 'holiday request', 'approvals'] },
  { label: '👥 People → Changelog', tab: 'people', sub: 'changelog', syn: ['edits', 'history', 'audit'] },
  // v132.47 — the seven sub-tabs that existed only as in-page buttons. They
  // always worked; they were just unfindable unless you knew which parent
  // page to open first. Search costs no screen space, so ALL of them go here.
  { label: '👥 People → Induction', tab: 'people', sub: 'induction', syn: ['onboarding', 'new starter', 'probation', 'induction board'] },
  { label: '👥 People → Courses', tab: 'people', sub: 'coursepasses', syn: ['courses', 'course passes', 'train the trainer', 'training academy', 'commitment', 'leadership quiz', 'leadership style'] },   // v133.58
  { label: '👥 People → Duty Manager', tab: 'people', sub: 'dmsignoff', syn: ['duty manager', 'dm sign off', 'dmso', 'sign-off', 'key holder'] },   // v133.16
  { label: '👥 People → Reviews', tab: 'people', sub: 'reviews', syn: ['appraisal', 'performance review', 'one to one', 'development'] },
  { label: '👥 People → Areas', tab: 'people', sub: 'areas', syn: ['area', 'region', 'territory', 'area manager'] },
  { label: '👥 People → D&G cases', tab: 'people', sub: 'dgcases', syn: ['disciplinary', 'grievance', 'investigation', 'case file', 'hearing', 'misconduct'] },
  { label: '👥 People → Exit interviews', tab: 'people', sub: 'exitinterviews', syn: ['leaver', 'resignation', 'exit', 'offboarding', 'why they left'] },
  { label: '✅ Operations → Compliance', tab: 'operations', sub: 'compliance', syn: ['compliance', 'completion', 'scorecard', 'missed checks'] },
  { label: '🗓 Scheduling → AI rota', tab: 'scheduling', sub: 'ai', syn: ['rota', 'shifts', 'generate', 'schedule', 'week plan'] },
  // v136.3 - AFTER the AI rota row: both labels start a word with "rota", and a bare "rota" still lands on the AI
  // rota first (pinned by tests/admin-nav-search_test.js). "store rota" lands here.
  { label: '🗓 Store rotas', tab: 'storerotas', syn: ['rota', 'store rota', 'shifts', 'who is working', 'week', 'cover', 'days off'] },
  { label: '🗓 Scheduling → Clock log', tab: 'scheduling', sub: 'clocklog', syn: ['clock in', 'clock out', 'attendance', 'hours', 'lates'] },
  { label: '🗓 Scheduling → Delivery', tab: 'scheduling', sub: 'delivery', syn: ['deliveries', 'stock arrival'] },
  { label: '🗓 Scheduling → Holidays', tab: 'scheduling', sub: 'holidays', syn: ['holiday', 'leave', 'annual leave', 'vacation', 'sick', 'apl'] },
  // ORDER MATTERS HERE. Both this label and the one above have a word
  // starting "holiday", so matchNav scores both 3 and the stable sort falls
  // back to array order. A bare "holiday" means the public-holiday admin, so
  // Scheduling must come FIRST and the tracker second. Pinned by
  // tests/admin-nav-search_test.js - do not re-sort this list alphabetically.
  { label: '👥 People → Holiday tracker', tab: 'people', sub: 'holiday', syn: ['holiday tracker', 'leave calendar', 'who is off'] },
  { label: '✅ Operations → Checklists', tab: 'operations', sub: 'checklists', syn: ['opening', 'closing', 'daily checks'] },
  { label: '✅ Operations → Logs', tab: 'operations', sub: 'logs', syn: ['store logs', 'fridge', 'temperature', 'incident'] },
  { label: '🎯 Targets', tab: 'targets', syn: ['budget', 'sales target', 'upload budget'] },
  { label: '📈 Area KPIs', tab: 'areakpis', syn: ['kpi', 'demo', 'add-on', 'batteries', 'gift wrap', 'performance'] },
  { label: '📦 Top 50', tab: 'top50', syn: ['best sellers', 'stock', 'soh', 'products'] },
  { label: '📷 Scan', tab: 'scan', syn: ['scan', 'camera', 'photo', 'barcode', 'product lookup', 'scan data', 'stock check'] },   // v136.19
  { label: '🏪 Stores', tab: 'stores', syn: ['store list', 'areas', 'contacts', 'phone', 'emails', 'opening hours'] },
  { label: '📋 Store Visits', tab: 'store-visits', syn: ['svr', 'visit report', 'rag', 'audit visit'] },
  { label: '📅 AM Planner', tab: 'amplanner', syn: ['planner', 'week planner', 'area manager', 'who to talk to'] },
  { label: '🕐 Cover check', tab: 'covercheck', syn: ['cover', 'manpower', 'staffing', 'sales by hour', 'right people', 'short', 'spare'] },   // v132.97
  { label: '⭐ Customer', tab: 'cxfeedback', syn: ['reviews', 'feedback', 'nps', 'ratings', 'customer voice'] },
  { label: '💬 Messages', tab: 'messages', syn: ['chat', 'email', 'inbox', 'send', 'kudos'] },
  { label: '📰 News', tab: 'news', syn: ['announcement', 'post', 'notice'] },
  { label: '✅ Tasks', tab: 'tasks', syn: ['todo', 'assign', 'jobs'] },
  { label: '📋 Surveys', tab: 'surveys', syn: ['questionnaire', 'poll'] },
  { label: '📊 Comms Report', tab: 'commsreport', syn: ['compliance', 'read rate', 'engagement'] },
  { label: '📽 Trade pack', tab: 'tradepack', syn: ['trade meeting', 'deck', 'slides', 'weekly meeting', 'presentation'] },

  { label: '🛍 Customer requests', tab: 'custrequests', syn: ['customer requests', 'asked for', 'we dont have', 'out of range', 'buying', 'wish list', 'demand'] },   // v132.79

  { label: '💬 JACK', tab: 'jack', syn: ['jack', 'ask jack', 'chat', 'assistant', 'read the brief', 'talk to jack'] },   // v132.70
  { label: '📧 Automated emails', tab: 'emailhub', syn: ['email hub', 'briefing email', 'audience', 'recipients', 'digest', 'weekly email', 'monthly email', 'send now'] },
  { label: '🎓 Training', tab: 'training', syn: ['weekly training', 'training session', 'quiz', 'learning', 'l&d', 'approve training', 'policy training'] },   // v132.1
  { label: '🏆 Achievements', tab: 'achievements', syn: ['badges', 'awards', 'stars', 'hearts', 'backfill', 'employee of the month'] },
  { label: '📖 Policies', tab: 'policies', syn: ['policy', 'handbook', 'rules', 'procedures'] },
  { label: '📱 Data Capture', tab: 'datacapture', syn: ['mobiles', 'emails captured', 'crm', 'bills', 'atv'] },
  { label: '💵 Petty Cash', tab: 'pettycash', syn: ['petty cash', 'expenses', 'claims', 'reimbursement', 'approval code', 'till'] },
  { label: '🔒 Concerns', tab: 'concerns', syn: ['whistleblowing', 'speak up', 'dg', 'grievance'] },
  { label: '🛡 Audit', tab: 'audit', syn: ['pin reveals', 'write audit', 'feed health', 'ingest', 'edit history'] },
];

// ── Pure: rank the index for a query. Word-prefix beats substring beats
// synonym; every typed word must land somewhere or the row is out. ──────────
export function matchNav(query, index) {
  const q = String(query || '').toLowerCase().trim();
  const rows = Array.isArray(index) ? index : NAV_INDEX;
  if (!q) return rows.slice(0, 8);
  const words = q.split(/\s+/).filter(Boolean);
  const scored = [];
  for (const e of rows) {
    const label = e.label.toLowerCase();
    const syns = (e.syn || []).map((s) => s.toLowerCase());
    let score = 0, ok = true;
    for (const w of words) {
      let ws = 0;
      if (label.split(/[^a-z0-9]+/).some((t) => t.indexOf(w) === 0)) ws = 3;      // label word-prefix
      else if (label.indexOf(w) !== -1) ws = 2;                                    // label substring
      else if (syns.some((s) => s.split(/\s+/).some((t) => t.indexOf(w) === 0))) ws = 1;  // synonym prefix
      if (!ws) { ok = false; break; }
      score += ws;
    }
    if (ok) scored.push({ e, score });
  }
  return scored.sort((a, b) => b.score - a.score).map((x) => x.e).slice(0, 8);
}

// ── UI. Injected once; jumps via the SAME globals the tab bar uses. ─────────
function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }
let _sel = 0, _hits = [];

function jump(e) {
  closePalette();
  const btn = document.querySelector('#mainTabs .tab[onclick*="\'' + e.tab + '\'"]');
  // A role-hidden tab has display:none — do not navigate somewhere the bar wouldn't.
  if (!btn || btn.style.display === 'none') return;
  if (typeof switchTab === 'function') switchTab(e.tab, btn);
  if (e.sub && typeof switchGroupSub === 'function') switchGroupSub(e.tab, e.sub);
}

function renderHits(q) {
  const list = document.getElementById('anavList');
  if (!list) return;
  _hits = matchNav(q);
  if (_sel >= _hits.length) _sel = Math.max(0, _hits.length - 1);
  list.innerHTML = _hits.length
    ? _hits.map((e, i) => '<div class="anav-row' + (i === _sel ? ' sel' : '') + '" onmouseenter="AdminNav.hover(' + i + ')" onclick="AdminNav.pick(' + i + ')">' + esc(e.label) + '</div>').join('')
    : '<div class="anav-none">Nothing matches — try "holiday", "rota", "pin"…</div>';
}

export function openPalette() {
  let ov = document.getElementById('anavOverlay');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'anavOverlay';
    ov.innerHTML = '<div id="anavCard">' +
      '<input id="anavInput" placeholder="Jump to… (try &quot;holiday&quot;, &quot;rota&quot;, &quot;pin reveals&quot;)" autocomplete="off">' +
      '<div id="anavList"></div>' +
      '<div class="anav-hint">↑↓ choose · Enter to go · Esc to close · Ctrl+K anytime</div></div>';
    document.body.appendChild(ov);
    ov.addEventListener('click', (ev) => { if (ev.target === ov) closePalette(); });
    const inp = ov.querySelector('#anavInput');
    inp.addEventListener('input', () => { _sel = 0; renderHits(inp.value); });
    inp.addEventListener('keydown', (ev) => {
      if (ev.key === 'ArrowDown') { ev.preventDefault(); _sel = Math.min(_sel + 1, _hits.length - 1); renderHits(inp.value); }
      else if (ev.key === 'ArrowUp') { ev.preventDefault(); _sel = Math.max(_sel - 1, 0); renderHits(inp.value); }
      else if (ev.key === 'Enter') { ev.preventDefault(); if (_hits[_sel]) jump(_hits[_sel]); }
      else if (ev.key === 'Escape') { closePalette(); }
    });
  }
  ov.style.display = 'flex';
  const inp = document.getElementById('anavInput');
  if (inp) { inp.value = ''; _sel = 0; renderHits(''); inp.focus(); }
}

export function closePalette() {
  const ov = document.getElementById('anavOverlay');
  if (ov) ov.style.display = 'none';
}

export function hover(i) { _sel = i; const inp = document.getElementById('anavInput'); renderHits(inp ? inp.value : ''); }
export function pick(i) { if (_hits[i]) jump(_hits[i]); }

// ── v129.65 — the 8-group tab bar (Matt approved the mock 6 Aug) ────────────
// The ORIGINAL #mainTabs stays in the DOM as the single source of truth for
// routing, role gates and per-account tab overrides — it is only HIDDEN.
// This bar is a veneer: a member click calls the SAME switchTab (+ optional
// switchGroupSub), and member visibility MIRRORS the original button's
// display (set by effectiveTabVisible), so a role-hidden tab is hidden here
// too and a group with no visible members disappears entirely.
// v134.9 - eight groups, not nine, and line icons, not emoji (the 15 Oct 2026 look-and-feel review: "9 groups over 30
// tabs", Training a group of one, emoji that look different on every device and are read aloud by screen readers).
// Each main group holds at most six; the set-up and oversight pages live under Settings. Role filtering is unchanged
// and needs nothing new: every member mirrors its original button's visibility, so an area manager never sees a
// Head Office page, and a group with nothing visible disappears.
const NAV_ICON_PATHS = {
  home: '<path d="M3 10l9-7 9 7v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/>',
  people: '<circle cx="9" cy="8" r="4"/><path d="M2 21v-1a6 6 0 0 1 6-6h2a6 6 0 0 1 6 6v1"/><path d="M16 4a4 4 0 0 1 0 8"/><path d="M22 21v-1a6 6 0 0 0-4-5.6"/>',
  rota: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
  trade: '<path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/>',
  stores: '<path d="M3 9l1.5-5h15L21 9"/><path d="M4 9v11h16V9"/><path d="M3 9h18"/><path d="M9 20v-6h6v6"/>',
  comms: '<path d="M3 11v2a1 1 0 0 0 1 1h3l6 4V6L7 10H4a1 1 0 0 0-1 1z"/><path d="M17 9a3 3 0 0 1 0 6"/><path d="M19.5 6.5a6.5 6.5 0 0 1 0 11"/>',
  training: '<path d="M2 9l10-5 10 5-10 5z"/><path d="M6 11v5c3 2 9 2 12 0v-5"/><path d="M22 9v6"/>',
  settings: '<path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0"/><circle cx="16" cy="6" r="2"/><circle cx="10" cy="12" r="2"/><circle cx="18" cy="18" r="2"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>',
  bug: '<rect x="7" y="8" width="10" height="12" rx="5"/><path d="M9 8V6a3 3 0 0 1 6 0v2M3 13h4M17 13h4M4 7l3 2M20 7l-3 2M4 19l3-2M20 19l-3-2"/>',
  idea: '<path d="M9 18h6M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.3h6c0-1 .4-1.8 1-2.3A7 7 0 0 0 12 2z"/>',   // v136.82: a lightbulb
};
export function navIcon(name) {
  const p = NAV_ICON_PATHS[name];
  return p ? '<svg class="gnav-ico" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + p + '</svg>' : '';
}
export const NAV_GROUPS = [
  { icon: 'home', label: 'Home', members: [
    { tab: 'dashboard', label: 'Dashboard' },
    { tab: 'ambrief', label: 'Morning brief' },
    { tab: 'storeoverview', label: 'Store overview' },   // one store before a visit (src/storeOverview)
    { tab: 'scorecard', label: 'Scorecard' },   // v136.41 - every store and person out of 100 (src/scorecardAdmin); v136.48 area managers too
    { tab: 'jack', label: 'Ask JACK about a store' },   // v132.70 - Head Office only
  ] },
  { icon: 'people', label: 'People', members: [
    { tab: 'people', sub: 'staff', label: 'Staff' },
    { tab: 'people', sub: 'toil_overview', label: 'Leave & TOIL balances' },
    { tab: 'people', sub: 'requests', label: 'Leave requests' },
    // v132.47: the HR case files keep a permanent slot - somebody reaches for them under time pressure.
    { tab: 'people', sub: 'dgcases', label: 'D&G cases' },
    { tab: 'people', sub: 'exitinterviews', label: 'Exit interviews' },
  ] },
  { icon: 'rota', label: 'Rota & Time', members: [
    // v136.3 - Store rotas first (every store's week, read only); the AI rules and generator relabelled "AI rota" so
    // the two are not confused (it was "Rota").
    { tab: 'storerotas', label: 'Store rotas' },
    { tab: 'scheduling', sub: 'ai', label: 'AI rota' },
    { tab: 'scheduling', sub: 'clocklog', label: 'Clock log' },
    { tab: 'covercheck', label: 'Cover check' },   // v132.97
    { tab: 'scheduling', sub: 'holidays', label: 'Holidays' },
    { tab: 'scheduling', sub: 'delivery', label: 'Deliveries' },
  ] },
  { icon: 'trade', label: 'Trade', members: [
    { tab: 'targets', label: 'Sales targets' },
    { tab: 'areakpis', label: 'KPIs' },
    { tab: 'top50', label: 'Top 50 stock' },
    { tab: 'scan', label: 'Scan' },   // v136.19 (Matt, 20 Oct 2026): its own place, not a Top 50 sub-tab
    { tab: 'custrequests', label: 'Customer requests' },   // v132.79
    { tab: 'cxfeedback', label: 'Customer reviews' },
    { tab: 'pettycash', label: 'Petty cash' },
  ] },
  { icon: 'stores', label: 'Stores', members: [
    { tab: 'stores', label: 'Store list & contacts' },
    { tab: 'store-visits', label: 'Store visits (SVR)' },
    { tab: 'amplanner', label: 'AM planner' },
    { tab: 'operations', sub: 'checklists', label: 'Checklists' },
    { tab: 'operations', sub: 'logs', label: 'Store logs' },
    { tab: 'operations', sub: 'compliance', label: 'Compliance' },   // v132.47
  ] },
  { icon: 'comms', label: 'Comms', members: [
    { tab: 'messages', label: 'Messages & email' },
    { tab: 'news', label: 'News' },
    { tab: 'tasks', label: 'Tasks' },
    { tab: 'surveys', label: 'Surveys' },
    { tab: 'commsreport', label: 'Comms report' },   // v136.13: news, tasks, surveys and journal compliance, where people look for it (also under Settings)
    { tab: 'tradepack', label: 'Trade pack' },   // v132.41
    { tab: 'achievements', label: 'Achievements' },
  ] },
  // v132.11 - Matt (19 Sep): 'Weekly training should be its own tab in admin'. Kept as its own group (v134.9).
  { icon: 'training', label: 'Training', members: [
    { tab: 'training', label: 'Training' },   // v132.1 - weekly training
  ] },
  { icon: 'settings', label: 'Settings', members: [
    { tab: 'people', sub: 'managers', label: 'Manager logins' },
    { tab: 'emailhub', label: 'Automated emails' },
    { tab: 'datacapture', label: 'Data capture' },
    { tab: 'commsreport', label: 'Comms report' },
    { tab: 'policies', label: 'Policies' },
    { tab: 'people', sub: 'changelog', label: 'Change history' },
    { tab: 'concerns', label: 'Concerns' },
    { tab: 'audit', label: 'Audit' },
  ] },
];

let _activeGroup = null;
function _origBtn(tab) {
  return document.querySelector('#mainTabs .tab[onclick*="\'' + tab + '\'"]');
}
function _memberVisible(m) {
  const b = _origBtn(m.tab);
  return !!(b && b.style.display !== 'none');
}
function _go(gi, mi) {
  const g = NAV_GROUPS[gi], m = g && g.members[mi];
  if (!m || !_memberVisible(m)) return;
  _activeGroup = gi;
  const btn = _origBtn(m.tab);
  if (typeof switchTab === 'function') switchTab(m.tab, btn);
  if (m.sub && typeof switchGroupSub === 'function') switchGroupSub(m.tab, m.sub);
  renderGroupedNav(m);
}
export function navGo(gi, mi) { _go(gi, mi); }

// Called after any switchTab (incl. the search palette's) to keep highlights honest.
// v136.13 (Matt, 19 Oct 2026: "can not find it ... think the button is hidden"): a tab can sit in MORE than one
// group (Settings' "Manager logins" is a page of the People tab). This used to jump to the FIRST group holding the
// tab, so opening Settings showed the People sub-menu and Settings' own items (Comms report, Automated emails,
// Policies, Concerns, Audit) could never be reached from the menu. Keep the group already open when it holds
// the tab; only move when it does not.
export function syncGroupNav(tabName) {
  const holds = (g) => !!g && g.members.some((m) => m.tab === tabName);
  if (!(_activeGroup != null && holds(NAV_GROUPS[_activeGroup]))) {
    const gi = NAV_GROUPS.findIndex(holds);
    if (gi !== -1) _activeGroup = gi;
  }
  renderGroupedNav();
}

export function renderGroupedNav(activeMember) {
  const bar = document.getElementById('groupedNav');
  if (!bar) return;
  if (_activeGroup == null) _activeGroup = 0;
  const groupsHtml = NAV_GROUPS.map((g, gi) => {
    if (!g.members.some(_memberVisible)) return '';
    return '<button type="button" class="gnav-g' + (gi === _activeGroup ? ' on' : '') + '" onclick="AdminNav.navOpenGroup(' + gi + ')">' + navIcon(g.icon) + '<span>' + esc(g.label) + '</span></button>';
  }).join('');
  const g = NAV_GROUPS[_activeGroup];
  const subsHtml = g ? g.members.map((m, mi) => {
    if (!_memberVisible(m)) return '';
    const on = activeMember && activeMember.tab === m.tab && activeMember.sub === m.sub;
    // v135.2: data-label keeps the English name for the bug report's "screen" (the button text may be Arabic)
    return '<button type="button" class="gnav-m' + (on ? ' on' : '') + '" data-label="' + esc(m.label) + '" onclick="AdminNav.navGo(' + _activeGroup + ',' + mi + ')">' + esc(m.label) + '</button>';
  }).join('') : '';
  const html = '<div class="gnav-row">' + groupsHtml +
    '<button type="button" class="gnav-g" onclick="AdminNav.openPalette()" title="Search admin (Ctrl+K)" aria-label="Search admin (Ctrl+K)">' + navIcon('search') + '</button>' +
    '<button type="button" class="gnav-g" onclick="AdminNav.openBugReport()" title="Report a bug in the admin app" aria-label="Report a bug in the admin app">' + navIcon('bug') + '</button>' +
    // v136.82 (a Head Office suggestion, 31 Oct 2026): ideas get their own button, not the bug form
    '<button type="button" class="gnav-g" onclick="AdminNav.openBugReport(\'idea\')" title="Suggest an improvement" aria-label="Suggest an improvement">' + navIcon('idea') + '</button></div>' +
    '<div class="gnav-subrow">' + subsHtml + '</div>';

  // v132.46 — SIGNATURE SKIP. Matt: "i couldnt click the group nav earlier".
  //
  // Writing innerHTML destroys and rebuilds every button. A click is a
  // mousedown and a mouseup on the SAME element, so a rebuild landing between
  // the two throws the click away: the button visibly depresses and nothing
  // happens. This render runs at boot, again at 400ms and again at 2000ms
  // (below) to pick up late role gates, so the whole first two seconds after
  // the admin app loads was eating clicks.
  //
  // Those two late passes almost always produce identical markup, because
  // role gates rarely change anything. So compare first and leave the DOM
  // alone when nothing changed. A real change (switching tab moves the `on`
  // class) still differs and still repaints.
  //
  // Same fix as the chat poll in v125.97. See the click-eating class in
  // docs/field-lessons.md: an overlay that swallows the click, or a repaint
  // that rebuilds the control mid-tap.
  if (bar._gnavHtml === html) return;
  bar._gnavHtml = html;
  bar.innerHTML = html;
}

// Opening a group jumps straight to its first VISIBLE member — one click, no
// dead "now pick again" state.
export function navOpenGroup(gi) {
  const g = NAV_GROUPS[gi];
  if (!g) return;
  const mi = g.members.findIndex(_memberVisible);
  if (mi !== -1) _go(gi, mi);
}

// ── v129.66 — 🐞 admin bug reports (AM ask, 6 Aug: "can we put bugs on
// admin user too") ──────────────────────────────────────────────────────────
// Same three questions, same POST /api/feedback, same Dev-tab triage as the
// staff app — with the ADMIN'S name/role attached and store = 'Admin app'
// so reports are attributable and filterable. No new backend.
//
// v132.115: screenshots on the admin bug report. A Head Office user, 30 Sep
// 2026: "i want to have provision or tab to insert photos. Screen Shots ... having
// images inserted make it Easy to identity the issues". The staff form has had
// them since v124.16 and the Worker already stores them (POST /api/feedback,
// feedback_photos), so this is the admin side only. The same rules as the staff
// form (src/notices/index.js): up to 3; images shrunk in the browser to a JPEG,
// longest edge 1400px, quality 0.7; PDF and Word read as they are; each at most
// 900,000 characters, the Worker's own limit (buildFeedbackPhotos), so nothing an
// admin attaches is silently dropped. tests/v132-115 pins the three places equal.
const ABUG_MAX = 3, ABUG_MAX_LEN = 900000;
let _abugShots = [];
let _abugKind = 'bug';   // v136.82: 'bug' or 'idea' (the suggestion button); the Worker already files both
const _toast = (m, k) => { if (typeof showToast === 'function') showToast(m, k); };
function _abugCompress(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('decode failed'));
      img.onload = () => {
        let w = img.width, h = img.height; const max = 1400;
        if (w > max || h > max) { if (w >= h) { h = Math.round(h * max / w); w = max; } else { w = Math.round(w * max / h); h = max; } }
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        try { resolve(c.toDataURL('image/jpeg', 0.7)); } catch (e) { reject(e); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
// PDF and Word, by type or by name (Windows often leaves the type empty)
export function abugDocMime(f) {
  const t = (f && f.type) || '', n = ((f && f.name) || '').toLowerCase();
  if (t === 'application/pdf' || /\.pdf$/.test(n)) return 'application/pdf';
  if (t === 'application/msword' || /\.doc$/.test(n)) return 'application/msword';
  if (t === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || /\.docx$/.test(n)) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return null;
}
function _abugReadRaw(file, mime) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^;]*;base64,/, 'data:' + mime + ';base64,'));
    reader.readAsDataURL(file);
  });
}
function _abugRender() {
  const wrap = document.getElementById('abugThumbs'); if (!wrap) return;
  wrap.innerHTML = _abugShots.map((src, i) => {
    const doc = /^data:application\/pdf/.test(src) ? 'PDF' : /^data:application\/msword/.test(src) ? 'DOC' : /^data:application\/vnd\.openxml/.test(src) ? 'DOCX' : null;
    return '<div class="abug-thumb">' + (doc ? '<span>📎 ' + doc + '</span>' : '<img src="' + src + '" alt="screenshot ' + (i + 1) + '">') +
      '<button type="button" aria-label="Remove attachment" onclick="AdminNav.abugRemove(' + i + ')">✕</button></div>';
  }).join('');
  const btn = document.getElementById('abugShotBtn'); if (btn) btn.style.display = _abugShots.length >= ABUG_MAX ? 'none' : '';
}
// add files (picked or pasted): skip anything not allowed or too big, with a message
async function _abugAdd(files, pasted) {
  let added = 0;
  for (const f of files) {
    if (_abugShots.length >= ABUG_MAX) { _toast('You can add up to ' + ABUG_MAX + ' attachments', 'info'); break; }
    const doc = pasted ? null : abugDocMime(f);
    if (!/^image\//.test((f && f.type) || '') && !doc) { _toast('Only images, PDF or Word files can be attached', 'info'); continue; }
    try {
      const url = doc ? await _abugReadRaw(f, doc) : await _abugCompress(f);
      if (url.length > ABUG_MAX_LEN) { _toast(doc ? 'That file is too large (max about 650KB)' : 'That image is too large even after shrinking: try a tighter crop', 'error'); continue; }
      _abugShots.push(url); added++;
    } catch (e) { _toast('Could not attach that file', 'error'); }
  }
  _abugRender();
  if (added && pasted) _toast('📷 Screenshot pasted');
}
export function abugPick(input) { const files = input && input.files ? Array.from(input.files) : []; if (input) input.value = ''; return _abugAdd(files, false); }
export function abugRemove(i) { _abugShots.splice(i, 1); _abugRender(); }
// paste a screenshot (Snipping Tool, Print Screen) while the form is open; text pastes are untouched
let _abugPasteBound = false;
function _abugBindPaste() {
  if (_abugPasteBound || typeof document === 'undefined') return; _abugPasteBound = true;
  document.addEventListener('paste', (e) => {
    try {
      const ov = document.getElementById('abugOverlay'); if (!ov || ov.style.display === 'none') return;   // only while the form is on screen
      const items = (e.clipboardData && e.clipboardData.items) || [], files = [];
      for (let i = 0; i < items.length; i++) if (items[i].kind === 'file' && /^image\//.test(items[i].type || '')) { const f = items[i].getAsFile(); if (f) files.push(f); }
      if (!files.length) return;
      e.preventDefault(); _abugAdd(files, true);
    } catch (err) { /* a paste must never break the form */ }
  });
}
export function openBugReport(kind) {
  _abugBindPaste();
  _abugKind = kind === 'idea' ? 'idea' : 'bug';
  let ov = document.getElementById('abugOverlay');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'abugOverlay';
    ov.innerHTML = '<div id="abugCard">' +
      '<div id="abugTitle" style="font-weight:800;font-size:15px;margin-bottom:2px">🐞 Report an admin-app bug</div>' +
      '<div id="abugSub" style="font-size:12px;color:var(--sub);margin-bottom:10px">Goes straight to the Dev console triage, with your name attached.</div>' +
      '<label id="abugDoingLbl">1. What were you doing?</label><textarea id="abugDoing" rows="2"></textarea>' +
      '<label id="abugExpectedLbl">2. What did you expect?</label><textarea id="abugExpected" rows="2"></textarea>' +
      '<div id="abugActualWrap"><label>3. What actually happened? (include any error text)</label><textarea id="abugActual" rows="3"></textarea></div>' +
      // v132.115: screenshots, like the staff form (up to 3; paste works too)
      '<label>Screenshots (optional, up to ' + ABUG_MAX + ')</label>' +
      '<div id="abugThumbs" class="abug-thumbs"></div>' +
      '<input type="file" id="abugShotInput" accept="image/*,.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" multiple hidden onchange="AdminNav.abugPick(this)">' +
      '<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap"><button type="button" class="gnav-m" id="abugShotBtn" onclick="document.getElementById(\'abugShotInput\').click()">📎 Add screenshot / file</button>' +
      '<span style="font-size:11.5px;color:var(--sub)">or paste one here (Ctrl+V)</span></div>' +
      '<div id="abugStatus" style="font-size:12px;min-height:18px;margin-top:6px"></div>' +
      '<div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px">' +
        '<button type="button" class="gnav-m" onclick="AdminNav.closeBugReport()">Cancel</button>' +
        '<button type="button" class="gnav-g on" id="abugSubmit" onclick="AdminNav.submitBugReport()">Submit report</button>' +
      '</div></div>';
    document.body.appendChild(ov);
    ov.addEventListener('click', (ev) => { if (ev.target === ov) closeBugReport(); });
  }
  // v136.82: one form, two jobs. An idea has no "what went wrong", so its third question is hidden.
  const idea = _abugKind === 'idea';
  const setText = (id, t) => { const el = document.getElementById(id); if (el) el.textContent = t; };
  setText('abugTitle', idea ? '💡 Suggest an improvement' : '🐞 Report an admin-app bug');
  setText('abugSub', idea ? 'Goes to the Dev console with your name attached. Ideas are read and answered, not just filed.' : 'Goes straight to the Dev console triage, with your name attached.');
  setText('abugDoingLbl', idea ? '1. What is it about?' : '1. What were you doing?');
  setText('abugExpectedLbl', idea ? '2. What would you like Shift to do, and how would it help?' : '2. What did you expect?');
  const aw = document.getElementById('abugActualWrap'); if (aw) aw.style.display = idea ? 'none' : '';
  const sb = document.getElementById('abugSubmit'); if (sb) sb.textContent = idea ? 'Send suggestion' : 'Submit report';
  ov.style.display = 'flex';
  const d = document.getElementById('abugDoing');
  if (d) d.focus();
}
export function closeBugReport() {
  const ov = document.getElementById('abugOverlay');
  if (ov) ov.style.display = 'none';
}
export async function submitBugReport() {
  const val = (id) => (document.getElementById(id) ? document.getElementById(id).value.trim() : '');
  const idea = _abugKind === 'idea';
  const doing = val('abugDoing'), expected = val('abugExpected'), actual = idea ? '(idea, no defect)' : val('abugActual');
  const status = document.getElementById('abugStatus');
  if (idea && (!doing || !expected)) { if (status) status.innerHTML = '<span style="color:var(--adm-red)">Please say what it is about and what you would like Shift to do.</span>'; return; }
  if (!doing || !expected || !actual) { if (status) status.innerHTML = '<span style="color:var(--adm-red)">All three answers are needed — the triage agent works from them.</span>'; return; }
  const btn = document.getElementById('abugSubmit');
  if (btn) btn.disabled = true;
  if (status) status.textContent = 'Sending…';
  const admin = (typeof currentAdmin !== 'undefined' && currentAdmin) || {};
  const payload = {
    kind: idea ? 'idea' : 'bug', doing: doing, expected: expected, actual: actual,
    user_name: admin.name || '(admin)', user_role: admin.role || 'admin',
    store: 'Admin app',
    screen: (function () { const on = document.querySelector('.gnav-m.on'); return (on && ((on.dataset && on.dataset.label) || on.textContent)) || 'admin'; })(),   // v135.2: the English name, not the Arabic on screen
    screenshots: _abugShots.slice(),   // v132.115: up to 3, already shrunk; the Worker re-checks the same limits
    app_version: (typeof APP_VERSION !== 'undefined' ? APP_VERSION : null),
    // v129.107 — was `ua:`, which is NOT a column on feedback_reports (the staff
    // app has always sent `user_agent`). PostgREST 400s on an unknown column, so
    // EVERY admin-app bug report since v129.66 died as a generic 500 and was
    // never filed — including the reports that would have told us. Same 300-char
    // trim as the staff side; the Dev tab + the GitHub issue read user_agent.
    user_agent: (typeof navigator !== 'undefined' && navigator.userAgent ? String(navigator.userAgent).slice(0, 300) : ''),
  };
  try {
    await DB.feedback.submit(payload);
    if (status) status.innerHTML = '<span style="color:var(--ok)">✅ Submitted — thank you. It lands in the Dev console triage.</span>';
    ['abugDoing', 'abugExpected', 'abugActual'].forEach((id) => { const el = document.getElementById(id); if (el) el.value = ''; });
    _abugShots = []; _abugRender();
    setTimeout(closeBugReport, 1600);
  } catch (e) {
    if (status) status.innerHTML = '<span style="color:var(--adm-red)">Could not send — ' + esc((e && e.message) || 'network error') + '. Try again.</span>';
  }
  if (btn) btn.disabled = false;
}

// Boot: 🔍 button in the tab bar + the global shortcut. Guarded so tests can
// import the pure parts with no DOM.
if (typeof document !== 'undefined') {
  const boot = () => {
    const bar = document.getElementById('mainTabs');
    // v129.65: the grouped bar replaces the visible 22-button row. #mainTabs
    // stays in the DOM (routing + role-gate truth) but is hidden; the grouped
    // bar renders after the role gates have set per-button visibility (the
    // 400ms retry window covers the async admin boot).
    if (bar && !document.getElementById('groupedNav')) {
      const g = document.createElement('div');
      g.id = 'groupedNav';
      bar.parentNode.insertBefore(g, bar);
      bar.style.display = 'none';
      renderGroupedNav();
      setTimeout(() => renderGroupedNav(), 400);    // after effectiveTabVisible pass
      setTimeout(() => renderGroupedNav(), 2000);   // late role/override application
      // Keep highlights honest when ANYTHING else calls switchTab (search
      // palette, in-page links): wrap once, delegate always.
      if (typeof window.switchTab === 'function' && !window.switchTab._gnavWrapped) {
        const orig = window.switchTab;
        const wrapped = function (name, el) { const r = orig(name, el); try { syncGroupNav(name); } catch (e) {} return r; };
        wrapped._gnavWrapped = true;
        window.switchTab = wrapped;
      }
    }
    document.addEventListener('keydown', (ev) => {
      if ((ev.ctrlKey || ev.metaKey) && String(ev.key).toLowerCase() === 'k') { ev.preventDefault(); openPalette(); }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
}
